/** @jsxImportSource @emotion/react */
import { TooltipWrapper } from 'components/atoms/TooltipWrapper'
import React from 'react'
import {
  billingAdjustmentCodes,
  billingAdjustmentGroups,
} from 'ts-common/constants/billingAdjustmentCodes'
import { billingRarcCodes } from 'ts-common/constants/billingRarcCodes'

const adjustmentGroupDescriptions = new Map(billingAdjustmentGroups.map((g) => [g.title, g.description]))
const adjustmentReasonDescriptions = new Map(billingAdjustmentCodes.map((c) => [c.code, c.description]))
const remarkDescriptions = new Map(billingRarcCodes.map((c) => [c.code, c.description]))

const codeCss = (hasDescription: boolean) =>
  hasDescription
    ? { cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 2 }
    : undefined

// CARCs a biller reads by name rather than by number: the badge says which
// write-off this is without a hover. Sequestration is kept apart from the
// contractual adjustment in finance's reconciliation (TES-3211).
const ADJUSTMENT_CODE_TAGS: Record<string, string> = { '253': 'Sequestration' }

export const adjustmentCodeLabel = (group?: string, reason?: string): string => {
  const code = `${group || 'N/A'}-${reason || 'N/A'}`
  const tag = reason ? ADJUSTMENT_CODE_TAGS[reason] : undefined

  return tag ? `${code} (${tag})` : code
}

export const AdjustmentCode = ({ group, reason }: { group?: string; reason?: string }) => {
  const groupDescription = group ? adjustmentGroupDescriptions.get(group) : undefined
  const reasonDescription = reason ? adjustmentReasonDescriptions.get(reason) : undefined
  const description = [groupDescription && `(${groupDescription})`, reasonDescription]
    .filter(Boolean)
    .join(' ')

  return (
    <TooltipWrapper text={description} show={Boolean(description)} maxWidth={440}>
      <span css={codeCss(Boolean(description))}>{adjustmentCodeLabel(group, reason)}</span>
    </TooltipWrapper>
  )
}

export const RemarkCode = ({ code }: { code: string }) => {
  const description = remarkDescriptions.get(code)

  return (
    <TooltipWrapper text={description || ''} show={Boolean(description)} maxWidth={440}>
      <span css={codeCss(Boolean(description))}>{code}</span>
    </TooltipWrapper>
  )
}

// Payer-side adjustment groups; PR (patient responsibility, e.g. deductible)
// on a $0-paid line is not a denial, it just shifts who owes.
const DENIAL_GROUPS = ['CO', 'OA', 'PI']

export const isDeniedAdjudication = (
  paidRefund: number | string | undefined,
  adjustments?: { group: string; reason?: string }[] | null,
): boolean =>
  (Number(paidRefund ?? 0) || 0) === 0 &&
  (adjustments || []).some((a) => DENIAL_GROUPS.includes(a.group))

export const DeniedBadge = () => (
  <span
    css={{
      display: 'inline-block',
      background: '#dc3545',
      color: '#fff',
      borderRadius: 3,
      fontSize: '0.688rem',
      fontWeight: 600,
      lineHeight: 1.5,
      padding: '0 4px',
      textTransform: 'uppercase',
    }}>
    Denied
  </span>
)

// A CAS pair as a biller types it: a two-letter group, a dash, a CARC.
const ADJUSTMENT_TOKEN = /^([A-Z]{2})-([A-Z0-9]+)$/

const splitAdjustmentTokens = (input?: string): string[] =>
  (input || '')
    .split(/[,;\s]+/)
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean)

// Parses manual entry like "CO-45, PR-1" into structured adjustment codes
export const parseAdjustmentCodesInput = (input?: string): { group: string; reason: string }[] => {
  return splitAdjustmentTokens(input).flatMap((token) => {
    const match = token.match(ADJUSTMENT_TOKEN)
    return match ? [{ group: match[1], reason: match[2] }] : []
  })
}

// Everything the parser would silently drop. Reported instead, because a code
// that disappears between typing and saving is worse than one that is refused.
export const findMalformedAdjustmentTokens = (input?: string): string[] =>
  splitAdjustmentTokens(input).filter((token) => !ADJUSTMENT_TOKEN.test(token))

const ADJUSTMENT_GROUPS = new Set(billingAdjustmentGroups.map((group) => group.title))
const REASON_CODES = new Set(billingAdjustmentCodes.map((code) => String(code.code).toUpperCase()))

// Codes that parse but name a group or a CARC that does not exist. Checked at
// the keyboard, while the person who typed them still has the EOB in hand.
export const findUnknownAdjustmentCodes = (input?: string): string[] =>
  parseAdjustmentCodesInput(input)
    .filter((code) => !ADJUSTMENT_GROUPS.has(code.group) || !REASON_CODES.has(code.reason))
    .map((code) => `${code.group}-${code.reason}`)

// Parses manual entry like "N211, MA01" into RARC codes
export const parseRemarkCodesInput = (input?: string): string[] => {
  return (input || '')
    .split(/[,;\s]+/)
    .map((token) => token.trim().toUpperCase())
    .filter(Boolean)
}

export const RemarkCodeList = ({ codes }: { codes?: (string | undefined)[] | null }) => {
  const filtered = (codes || []).filter((c): c is string => Boolean(c))

  if (!filtered.length) {
    return null
  }

  return (
    <>
      {filtered.map((code, i) => (
        <React.Fragment key={`${code}-${i}`}>
          {i > 0 && ', '}
          <RemarkCode code={code} />
        </React.Fragment>
      ))}
    </>
  )
}
